"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-9 w-20 bg-muted animate-pulse" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-10 w-max items-center justify-center bg-transparent px-4 py-2 text-sm font-default font-bold uppercase tracking-widest text-foreground transition-all hover:text-primary hover:underline decoration-primary decoration-2 underline-offset-8 focus:bg-accent focus:text-accent-foreground outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground">
          <Menu className="size-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l-2 border-primary">
        <div className="flex flex-col gap-6 py-6">
          <Link to="/" className="block" onClick={() => setIsOpen(false)}>
            <span className="text-2xl font-bold text-primary">ECG</span>
          </Link>
          
          <nav className="flex flex-col gap-4 mt-8">
            {loading ? <div className="flex flex-col gap-4">
                {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse" />)}
              </div> : menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-default font-bold uppercase tracking-wider text-foreground hover:text-primary transition-colors py-2 border-b border-border/50" onClick={() => setIsOpen(false)}>
                  {item.label}
                </Link>)}
          </nav>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="225011" id="header" className="sticky top-0 z-50 w-full transition-all duration-500 border-b border-border/10 bg-background py-4 dark">
      <MenuProvider menu_id="36">
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          
          <div className="flex-shrink-0">
            <Link to="/" className="block transition-transform hover:scale-105">
              <span className="text-2xl font-bold text-primary">ECG</span>
            </Link>
          </div>

          <div className="hidden md:flex flex-1 justify-center px-6">
            <DesktopNav />
          </div>

          <div className="flex items-center gap-4 flex-shrink-0">
            <MobileNav />
          </div>

        </div>
      </MenuProvider>
    </section>;
}